import { css } from 'styled-components';
import withConformsTo from '@dbh/with-conforms-to-for-production-www';
import { valuesSpacing, valuesSize, Z_INDEX_THEME_KEY, fonts } from '@dbh/theme-values';
import PropTypes from 'prop-types';
import 'lodash/isString';
import 'invariant';
import { simpleGetInObject } from '@dbh/lodash-extra';
import { isValidSpacingValue } from '@dbh/validation';
import _noop from 'lodash/noop';
import { breakpoints, breakpointsKeys } from '@dbh/breakpoints';
import '@dbh/create-custom-react-prop-type';
import { allPropTypesMustPass } from '@dbh/generic-types';
import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/esm/taggedTemplateLiteralLoose';
import nanoMemoize from 'nano-memoize';
import media from '@dbh/media-queries';

const withHiddenScrollbars=/*#__PURE__*/css(["-ms-overflow-style:none;scrollbar-width:none;&::-webkit-scrollbar{display:none;}"]);var withHiddenScrollbars$1 = withHiddenScrollbars;

const ICON_POSITION_VALUES=["left","top","right","bottom"];[...valuesSpacing,...valuesSize];const withIconWidthAndHeightCssHelperPropTypesShape={};const iconPositionValuePropType=PropTypes.oneOf(ICON_POSITION_VALUES);const withIconHelperPropType={};

const printPseudoSelector=withConformsTo("printPseudoSelector",[],a=>-1<["left","top"].indexOf(a)?"&:before":"&:after");var printPseudoSelector$1 = printPseudoSelector;

/**
 * React `prop-types` validator, checks that the user is not passing many
 * spacing values one by one, and if that is the case, it encourages using
 * a shorthand instead, passed to the `margin` or `padding` prop.
 * @see {@link https://github.com/facebook/prop-types}.
 * @param {Object} source Passed by `prop-types`.
 * @param {string} propKey Passed by `prop-types`.
 * @param {string} shapeName Passed by `prop-types`.
 * @param {string} propLocation Passed by `prop-types`.
 * @param {string} propName Passed by `prop-types`.
 * @return {Error?} If `source` does not use a spacing shorthand but we think it
 * should.
 */const encourageSpacingShorthandPropType=_noop;const spacingPropType=_noop;[...valuesSize,"0",0];const sizeByBreakpointValuesAreValidPropType=_noop,sizeByBreakpointValuesAreNotRedundantPropType=_noop;/**
 * @example
 * ```js
 * // Error:
 * {
 *  xs: 'ff6',
 *  sm: 'something',
 *  something: 'ff3',
 *  lg: 'ff2',
 * }
 * ```
 * @example
 * ```js
 * // Correct:
 * {
 *  xs: 'ff6',
 *  sm: 'ff3',
 *  md: 'ff1',
 *  lg: 'ff2',
 * }
 * ```
 */const sizeByBreakpointPropType=allPropTypesMustPass(sizeByBreakpointValuesAreValidPropType.isRequired,sizeByBreakpointValuesAreNotRedundantPropType.isRequired);

const getSizeValue=withConformsTo("getSizeValue",[],(a,b)=>{const c=b||a.iconSize||a.size,d=a.theme.fonts.size[c],e=a.theme.spacing[c],f=d||e;// Handle the special case where the size is `inherit`, in this case we
// want to use `1em` as the return value since this value will be used outside
// of the `font-size` property.
return "inherit"===f?"1em":f});var getSizeValue$1 = getSizeValue;

const makeWidthHeight=a=>a.iconInsideCircle?"calc("+getSizeValue$1(a)+" * 2)":getSizeValue$1(a),withIconWidthAndHeightCssHelper=/*#__PURE__*/css(["width:",";height:",";"],makeWidthHeight,makeWidthHeight);var withIconWidthAndHeightCssHelper$1 = withIconWidthAndHeightCssHelper;

const withIcon=/*#__PURE__*/css(["","{content:'';flex-shrink:0;background-image:url('","');background-size:",";background-repeat:no-repeat;float:left;margin:",";"," background-position:center;"," ","};",";"],a=>{let{iconPosition:b}=a;return printPseudoSelector$1(b)},a=>{let{iconSrc:b}=a;return b},a=>getSizeValue$1(a),a=>{let{theme:{textWithIcon:{iconMargin:b}}}=a;return b},withIconWidthAndHeightCssHelper$1,a=>{let{iconInsideCircle:b,theme:{colors:e},iconBorderColor:c,color:d}=a;return b&&"\n    border-radius: 50%;\n    border: 2px solid "+e[c||d]+";\n    background-position: center;\n  "},a=>{let{iconBackgroundColor:b,theme:{colors:c}}=a;return b&&"background-color: "+c[b]+";"},a=>{let{hoverIconSrc:b,iconPosition:c}=a;return b&&"\n    &:hover {\n      "+printPseudoSelector$1(c)+" {\n        background-image: url('"+b+"');\n      };\n    };\n  "});

/**
 * Given `props` and `key` retrieve `font-size` value.
 * @param {Object} props Component's `props`.
 * @param {string} key The key of desired `font-size`.
 * @return {string} A valid CSS `font-size` value.
 */const themeFontSize=withConformsTo("themeFontSize",[],(a,b)=>a.theme.fonts.size[b]);/**
 * Given `props` and `key` retrieve `font-style` value.
 * @param {Object} props Component's `props`.
 * @param {string} key The key of desired `font-style`.
 * @return {string} A valid CSS `font-style` value.
 */const themeFontStyle=withConformsTo("themeFontStyle",[],(a,b)=>a.theme.fonts.style[b]);// Do not define `themeTextAlign` and `themeTextTransform`, use the values
// directly in the `CSS`.
/**
 * Given `props` and `key` retrieve `line-height` value.
 * @param {Object} props Component's `props`.
 * @param {string} key The key of desired `line-height`.
 * @return {string} A valid CSS `line-height` value.
 */const themeLineHeight=withConformsTo("themeLineHeight",[],(a,b)=>a.theme.fonts.lineHeight[b]);// Colors.
/**
 * Given `props` and `key` retrieve `color` value.
 * @param {Object} props Component's `props`.
 * @param {string} key The key of desired `color`.
 * @return {string} A valid CSS `color` value.
 */const themeColor=withConformsTo("themeColor",[],(a,b)=>a.theme.colors[b]);// Spacing.
/**
 * Given `props` and `keys` retrieve `spacing` value string.
 * @param {Object} props Component's `props`.
 * @param {string} keys The CSS spacing of desired `spacing`. @example `sp1 sp2 0 sp3`.
 * @return {string} A valid CSS `margin` or `padding` shorthand value.
 */const themeSpacingShorthand=withConformsTo("themeSpacingShorthand",[],(a,b)=>b.split(" ").map(b=>a.theme.spacing[b]).join(" "));/**
 * Given `props` and `key` retrieve `spacing` value.
 * @param {Object} props Component's `props`.
 * @param {string|string[]} key The key(s) of desired `spacing`.
 * @return {string} A valid CSS `spacing` value.
 */const themeSpacing=(a,b)=>{const c=!isValidSpacingValue(b)&&b.includes(" ");return c?themeSpacingShorthand(a,b):a.theme.spacing[b]};/**
 * Given a path in a `styled-components` theme, returns a factory function to
 * create a `styled-components` theme getter.
 * @example
 * ```js
 * const getFromLocalTheme = createGetFromLocalTheme(['theme', 'someComponent']);
 * const SomeComponent = styled`
 *    display: flex;
 *    background-color: ${getFromLocalTheme(['backgroundColor'])};
 * `;
 * ```
 * @param {Array} localThemePath @example `['theme', 'someComponent', 'wrapper']`.
 * @return {Function} This accepts an `Array` or a variable number of strings
 * and returns an accessor to the theme property value, given react component
 * props including `theme`.
 */const createGetFromLocalTheme=withConformsTo("createGetFromLocalTheme",[],a=>function(){for(var b=arguments.length,c=Array(b),d=0;d<b;d++)c[d]=arguments[d];const e=Array.isArray(c[0])?c[0]:c;const f=[...a,...e];return a=>{const b=simpleGetInObject(a,f);return b}});/**
 * Generates accessors to `theme/zIndex` properties given a `React` component
 * props.
 * @example
 * ```js
 * import { getThemeZIndex } from '@dbh/styled-helpers';
 *
 * const styledCompont = styled.div`
 *   z-index: ${getThemeZIndex(['path', 'to', 'zIndex'])};
 * `;
 * ```
 * @param {string[]} path Keys that reference the `z-index` value.
 * @return {Function} Given a `path` to the `zIndex` returns a function that
 * accepts the component prop and returns the requested `z-index` value,
 * extracted from `theme/zIndices`;.
 */const getThemeZIndex=createGetFromLocalTheme(["theme",Z_INDEX_THEME_KEY]);

const withPadding=/*#__PURE__*/css(["",";"],a=>"\n    "+(a.padding?"padding: "+themeSpacing(a,a.padding)+";":"")+"\n    "+(a.paddingTop?"padding-top: "+a.theme.spacing[a.paddingTop]+";":"")+"\n    "+(a.paddingRight?"padding-right: "+a.theme.spacing[a.paddingRight]+";":"")+"\n    "+(a.paddingBottom?"padding-bottom: "+a.theme.spacing[a.paddingBottom]+";":"")+"\n    "+(a.paddingLeft?"padding-left: "+a.theme.spacing[a.paddingLeft]+";":"")+"\n  ");const withPaddingHelperPropType={padding:spacingPropType,paddingTop:PropTypes.oneOf(valuesSpacing),paddingRight:PropTypes.oneOf(valuesSpacing),paddingBottom:PropTypes.oneOf(valuesSpacing),paddingLeft:PropTypes.oneOf(valuesSpacing),___encouragePaddingShorthand:encourageSpacingShorthandPropType};

const withPaddingShorthand=/*#__PURE__*/css(["",";"],a=>"\n    "+(a.padding?"padding: "+themeSpacing(a,a.padding)+";":"")+"\n  ");

const withMargin=/*#__PURE__*/css(["",";"],a=>"\n    "+(a.margin?"margin: "+themeSpacing(a,a.margin)+";":"")+"\n    "+(a.marginTop?"margin-top: "+a.theme.spacing[a.marginTop]+";":"")+"\n    "+(a.marginRight?"margin-right: "+a.theme.spacing[a.marginRight]+";":"")+"\n    "+(a.marginBottom?"margin-bottom: "+a.theme.spacing[a.marginBottom]+";":"")+"\n    "+(a.marginLeft?"margin-left: "+a.theme.spacing[a.marginLeft]+";":"")+"\n  ");const withMarginHelperPropType={margin:spacingPropType,marginTop:PropTypes.oneOf(valuesSpacing),marginRight:PropTypes.oneOf(valuesSpacing),marginBottom:PropTypes.oneOf(valuesSpacing),marginLeft:PropTypes.oneOf(valuesSpacing),___encourageMarginShorthand:encourageSpacingShorthandPropType};

const propsToLabel={size:"size",color:"color",weight:"weight",align:"align",lineHeight:"lineHeight",paddingTop:"p_top",paddingRight:"p_right",paddingBottom:"p_bottom",paddingLeft:"p_left",transform:"transform"},propsDisplayed=Object.keys(propsToLabel);withConformsTo("makeHint",[],a=>{const b=propsDisplayed.reduce((b,c)=>(a[c]&&b.push(propsToLabel[c]+": "+a[c]+" | "),b),[]),c=a.intlFormatter?b.join("")+"ADMIN":b;return c});const withDevelopmentHints="";

const BREAKPOINTS_WIDTHS=breakpoints;/**
 * Accept a method to extract the breakpoint from the props and returns a helper
 * to be used in `styled-components` templates.
 * The helper sets the `max-width` property according to the selected breakpoint.
 * @param {Function} breakpointGetter Receives the props and returns the breakpoint.
 * @return {Function} A helper that can be used in styled-component templates.
 */const withBreakpointMaxWidth=withConformsTo("withBreakpointMaxWidth",[],a=>b=>{const c=a(b),d=BREAKPOINTS_WIDTHS[c];return d?"max-width: "+d+"px;":""});

var _templateObject,_templateObject2,_templateObject3,_templateObject4;const createStaticContainedHelper=nanoMemoize(withConformsTo("createStaticContainedHelper",[],a=>css(["width:100%;"," "," "," ",""],media.sm(_templateObject||(_templateObject=_taggedTemplateLiteralLoose(["max-width: ",""])),a&&a[0]),media.md(_templateObject2||(_templateObject2=_taggedTemplateLiteralLoose(["max-width: ",""])),a&&a[1]),media.lg(_templateObject3||(_templateObject3=_taggedTemplateLiteralLoose(["max-width: ",""])),a&&a[2]),media.xl(_templateObject4||(_templateObject4=_taggedTemplateLiteralLoose(["max-width: ",""])),a&&a[3]))));const DEFAULT_CONTAINING=["100%","100%","90%","1068px"];const [,,,DEFAULT_MAX_WIDTH_XL]=DEFAULT_CONTAINING;const defaultContainedHelper=createStaticContainedHelper(DEFAULT_CONTAINING);

/**
 * Hides the element for all devices except for screen readers. Useful when it
 * is mandatory to have a semantic `HTML` in the `DOM` but it doesn't conform
 * to the `UI` design. Heavily inspired by `Bootstrap` `.sr-only` class.
 * @see {@link https://a11yproject.com/posts/how-to-hide-content/}
 * @see {@link https://tinyurl.com/y6348bls}
 */const screenReaderOnly=/*#__PURE__*/css(["position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;"]);

/**
 * @typedef {{
 * xs: string,
 * sm: string?,
 * md: string?,
 * lg: string?,
 * xl: string?,
 * xxl: string?,
 * xxxl: string?
 * }} SizeByBreakpoint
 */const{size}=fonts;/**
 * Returns a `CSS` string that uses media queries, and sets font sizes in one or
 * more different breakpoints.
 * @param {SizeByBreakpoint} sizeByBreakpoint Includes the font sizes specified
 * for one or more breakpoints.
 * @return {string} The `css` that can be embedded in `styled` declarations.
 */const fontSizeByBreakpointHelper=withConformsTo("fontSizeByBreakpointHelper",[],a=>{const b=[0,"0"].includes(a.xs)?"0":size[a.xs],c=breakpointsKeys.reduce((b,c)=>{let d=b;// @example `ff1`.
const e=a[c];if(!e)return b;const f=[0,"0"].includes(e)?"0":size[e];return d+="\n      @media (min-width: "+breakpoints[c]+"px) {\n        font-size: "+f+";\n      }",d},"font-size: "+b+";");return css(["",";"],c)});

export { DEFAULT_CONTAINING, DEFAULT_MAX_WIDTH_XL, createGetFromLocalTheme, createStaticContainedHelper, defaultContainedHelper, encourageSpacingShorthandPropType, fontSizeByBreakpointHelper, getSizeValue$1 as getSizeValue, getThemeZIndex, iconPositionValuePropType, printPseudoSelector$1 as printPseudoSelector, screenReaderOnly, sizeByBreakpointPropType, spacingPropType, themeColor, themeFontSize, themeFontStyle, themeLineHeight, themeSpacing, withBreakpointMaxWidth, withDevelopmentHints, withHiddenScrollbars$1 as withHiddenScrollbars, withIcon, withIconHelperPropType, withIconWidthAndHeightCssHelper$1 as withIconWidthAndHeightCssHelper, withIconWidthAndHeightCssHelperPropTypesShape, withMargin, withMarginHelperPropType, withPadding, withPaddingHelperPropType, withPaddingShorthand };
